import { useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, apiBaseUrl, getErrorMessage } from "@/lib/api";
import { useAdminPager } from "@/lib/useAdminPager";

const fmtInr = (v) =>
  Number(v || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

export default function TdsTransactions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [module, setModule] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pager = useAdminPager();

  async function load() {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: String(pager.limit), skip: String(pager.skip) });
      if (module) params.set("module", module);
      if (search) params.set("search", search);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(`/api/admin/tds/transactions?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      pager.setTotal(data.total || 0);
    } catch (err) {
      setError(getErrorMessage(err));
      setItems([]);
      setTotal(0);
      pager.setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pager.page]);

  const exportUrl = () => {
    const params = new URLSearchParams();
    if (module) params.set("module", module);
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    const base = apiBaseUrl();
    return `${base}/api/admin/tds/transactions/export.csv?${params}`;
  };

  return (
    <AdminShell title="TDS transactions" subtitle={`${total} deductions on record`}>
      {error ? (
        <div className="mb-4 text-[13px] text-red-400">{error}</div>
      ) : null}
      <Panel className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-[12px]">
            <span className="text-muted-foreground block mb-1">Module</span>
            <select
              value={module}
              onChange={(e) => setModule(e.target.value)}
              className="h-[36px] px-3 bg-background border border-line rounded-md"
            >
              <option value="">All</option>
              <option value="spot">Spot</option>
              <option value="p2p">P2P</option>
              <option value="otc">OTC</option>
              <option value="escrow">Escrow</option>
            </select>
          </label>
          <label className="text-[12px]">
            <span className="text-muted-foreground block mb-1">From</span>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="h-[36px] px-3 bg-background border border-line rounded-md" />
          </label>
          <label className="text-[12px]">
            <span className="text-muted-foreground block mb-1">To</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="h-[36px] px-3 bg-background border border-line rounded-md" />
          </label>
          <label className="text-[12px] flex-1 min-w-[180px]">
            <span className="text-muted-foreground block mb-1">Search</span>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Trade / user / TDS id"
                className="w-full h-[36px] pl-9 pr-3 bg-background border border-line rounded-md"
              />
            </div>
          </label>
          <Btn onClick={load} data-testid="tds-txn-filter">Apply filters</Btn>
          <a href={exportUrl()} target="_blank" rel="noreferrer">
            <Btn data-testid="tds-txn-export"><Download size={14} /> CSV</Btn>
          </a>
        </div>
      </Panel>

      <Panel className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="tds-txn-table">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-line">
              <th className="p-3">User</th>
              <th className="p-3">Module</th>
              <th className="p-3">Trade amount</th>
              <th className="p-3">TDS %</th>
              <th className="p-3">TDS amount</th>
              <th className="p-3">Side</th>
              <th className="p-3">Trade ID</th>
              <th className="p-3">Time</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No TDS transactions yet</td></tr>
            ) : items.map((r) => (
              <tr key={r.tds_tx_id} className="border-b border-line/50 hover:bg-background/50">
                <td className="p-3">
                  <div className="font-medium">{r.user_email || r.user_id}</div>
                  <div className="text-muted-foreground">{r.user_name}</div>
                </td>
                <td className="p-3 uppercase">{r.module}</td>
                <td className="p-3 tabular-nums">{fmtInr(r.trade_amount)}</td>
                <td className="p-3">{r.tds_percentage}%</td>
                <td className="p-3 tabular-nums text-down">{fmtInr(r.tds_amount)}</td>
                <td className="p-3">{r.deducted_from}</td>
                <td className="p-3 font-mono text-[11px]">{r.trade_id}</td>
                <td className="p-3 text-muted-foreground">{r.created_at?.slice(0, 19)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          loading={loading}
          onPage={pager.setPage}
          testId="tds-pager"
        />
      </Panel>
    </AdminShell>
  );
}
